import React, {useState, useEffect, useMemo} from 'react';
import {FmlxTable} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import moment from 'moment';
import {Button} from 'react-bootstrap';
import JSZip from 'jszip';

import {DrawerButtonTitle} from '../Common';

import {HeaderLayout} from '../../components';
import PageLayout from '../../components/Layout/PageLayout';

import {Feedback} from '../../constants/dataEnum';

import withIntl from '../../lib/withIntl';

import {hideLoading, showLoading, showSnackbar} from '../../stores/actions/appStateAction';
import {hideModal, showModal} from '../../stores/actions/modalStateAction';

import {
  readDispenseReports,
  deleteDispenseReports,
  downloadDispenseReports,
  updateDispenseReport,
} from '../../stores/business/dispenseReportExplorerBusiness';

import './DispenseReportExplorer.scss';

const mapDispatchToProps = (dispatch) => ({
  onHideLoading: bindActionCreators(hideLoading, dispatch),
  onShowLoading: bindActionCreators(showLoading, dispatch),
  onShowSnackbar: bindActionCreators(showSnackbar, dispatch),
  modalStateActionShowModal: bindActionCreators(showModal, dispatch),
  modalStateActionHideModal: bindActionCreators(hideModal, dispatch),
  dispenseReportExplorerBusinessRead: bindActionCreators(readDispenseReports, dispatch),
  dispenseReportExplorerBusinessDelete: bindActionCreators(deleteDispenseReports, dispatch),
  dispenseReportExplorerBusinessDownload: bindActionCreators(downloadDispenseReports, dispatch),
  dispenseReportUpdateDispenseReport: bindActionCreators(updateDispenseReport, dispatch),
});

function DispenseReportExplorer({
  onHideLoading,
  onShowLoading,
  onShowSnackbar,
  modalStateActionShowModal,
  modalStateActionHideModal,
  dispenseReportExplorerBusinessRead,
  dispenseReportExplorerBusinessDelete,
  dispenseReportExplorerBusinessDownload,
  dispenseReportUpdateDispenseReport,
  intl,
}) {
  const [dataSource, setDataSource] = useState([]);
  const [sortedDataSource, setSortedDataSource] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortValue, setSortValue] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const pageSizeFormat = [10, 20, 30, 40, 50];

  const [refetch, setRefetch] = useState(null);

  const dateFormat = 'YYYY-MM-DD HH:mm:ss';

  const triggerRefetch = () => {
    setTimeout(() => {
      setRefetch(new Date().toString());
    }, 1);
  };

  const fetchData = async () => {
    setIsLoading(true);

    const result = await dispenseReportExplorerBusinessRead(0, 1000, null, true);

    let data = [];

    if (result?.payload?.items) {
      data = result.payload.items.map((e) => ({
        ...e,
        _original: e,
        runBy: e.user.name,
      }));
    }

    setDataSource(data);
    setSortedDataSource(data);

    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!refetch) {
      return;
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch]);

  useEffect(() => {
    let newSortedDataSource = [...dataSource];

    newSortedDataSource = newSortedDataSource.sort((a, b) => {
      let objA = a[sortValue];
      let objB = b[sortValue];

      switch (sortValue) {
        case 'createdTime':
          objA = moment(a.createdTime).format(dateFormat);
          objB = moment(b.createdTime).format(dateFormat);

          break;
        default:
          break;
      }
      return sortType === FmlxTable.SortType.ASC ? objA?.localeCompare(objB) : objB?.localeCompare(objA);
    });

    setSortedDataSource(newSortedDataSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortValue, sortType, dataSource]);

  const deleteHandler = async (ids) => {
    onShowLoading();

    const result = await dispenseReportExplorerBusinessDelete(ids);

    onHideLoading();

    if (!result || result?.code !== 0) {
      onShowSnackbar(Feedback.ERROR, result?.description ?? 'Something happened');

      return;
    }

    triggerRefetch();

    onShowSnackbar(Feedback.SUCCESS, intl.formatMessage({id: 'dispenseReportExplorer.delete.notification.success'}));
  };

  const showDeleteConfirmation = () => {
    const rowIds = selectedRows.map((e) => e.id);

    const title = intl.formatMessage({id: 'dispenseReportExplorer.deleteModal.title'});
    const content = intl.formatMessage(
      {id: 'dispenseReportExplorer.deleteModal.content'},
      {count: `${rowIds.length} Report${rowIds.length > 1 ? 's' : ''}`},
    );

    modalStateActionShowModal(
      title,
      content,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          text: intl.formatMessage({id: 'dispenseReportExplorer.deleteModal.ok'}),
          handlerClick: () => {
            modalStateActionHideModal();
            deleteHandler(rowIds);
          },
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          text: intl.formatMessage({id: 'dispenseReportExplorer.deleteModal.cancel'}),
          handlerClick: () => {
            modalStateActionHideModal();
          },
        },
      ],
      () => modalStateActionHideModal(),
      '',
      true,
    );
  };

  const exportHandler = async () => {
    const downloadFile = async (content, fileName) => {
      const url = window.URL.createObjectURL(new Blob([content]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    };

    const rowIds = selectedRows.map((e) => e.id);

    onShowLoading();
    const result = await dispenseReportExplorerBusinessDownload(rowIds);

    onHideLoading();

    const reports = result?.payload;

    if (!reports) {
      onShowSnackbar(Feedback.ERROR, result?.description ?? 'Something happened');

      return;
    }

    const {name: protocolRunBy} = reports[0].user ?? {};

    const fileName =
      reports.length > 1
        ? `Dispense Report_${moment().format(dateFormat)}.zip`
        : `Dispense Report${protocolRunBy ? `_${protocolRunBy}` : ''}_${reports[0].name}_${moment.utc(reports[0].createdTime).local().format(dateFormat)}.csv`;

    if (reports.length === 1) {
      const report = reports[0];
      downloadFile(report.content, fileName);
      await dispenseReportUpdateDispenseReport(report.id, {IsDownloaded: true}, report.user.userId);
    } else {
      const dateFormatZippedFile = 'YYYY-MM-DD HH_mm_ss';
      const zip = new JSZip();

      reports.forEach((e) => {
        zip.file(`${e.name}_${moment.utc(e.createdTime).local().format(dateFormatZippedFile)}.csv`, e.content);
      });

      zip.generateAsync({type: 'blob'}).then((content) => {
        downloadFile(content, fileName);
      });

      reports.forEach(async (report) => {
        await dispenseReportUpdateDispenseReport(report.id, {IsDownloaded: true}, report.user.userId);
      });
    }

    onShowSnackbar(Feedback.INFO, intl.formatMessage({id: 'dispenseReportExplorer.export.notification.success'}));
  };

  const COLUMNS = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Date',
        accessor: 'createdTime',
        // eslint-disable-next-line react/prop-types, react/no-unstable-nested-components
        Cell: ({value}) => <>{moment.utc(value).local().format(dateFormat)}</>,
      },
      {
        Header: 'Run By',
        accessor: 'runBy',
      },
    ],
    [],
  );

  return (
    <PageLayout>
      <PageLayout.Header>
        <HeaderLayout>
          <HeaderLayout.Left>
            <DrawerButtonTitle title={intl.formatMessage({id: 'dispenseReportExplorer.title'})} />
          </HeaderLayout.Left>
          <HeaderLayout.Right>
            <Button
              className="labware-manager-button secondary-button"
              disabled={!selectedRows.length}
              onClick={() => {
                showDeleteConfirmation();
              }}
            >
              {intl.formatMessage({id: 'dispenseReportExplorer.header.delete'})}
            </Button>
            <Button
              className="labware-manager-button secondary-button"
              disabled={!selectedRows.length}
              onClick={() => {
                exportHandler();
              }}
            >
              {intl.formatMessage({id: 'dispenseReportExplorer.header.export'})}
            </Button>
          </HeaderLayout.Right>
        </HeaderLayout>
      </PageLayout.Header>
      <PageLayout.Content>
        <FmlxTable
          type={FmlxTable.Type.MULTISELECT}
          data={sortedDataSource}
          columns={COLUMNS}
          selectable
          multiSelect
          showCheckBoxSelection
          getSelectedRow={(selectedItems) => {
            setSelectedRows(selectedItems);
          }}
          getSortValue={(e) => {
            setSortValue(e.column);
            setSortType(e.type);
          }}
          showPagination={false}
          pageSizes={pageSizeFormat}
          enableDragSelection={false}
          placeholder={
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img src="./svg/protocol-execution-report/report-not-found.svg" alt="report not found icon" />
              <div
                style={{
                  fontSize: 24,
                  color: '#707070',
                  marginTop: 40,
                }}
              >
                Protocol Execution Report is Empty
              </div>
            </div>
          }
          isLoading={isLoading}
          showToolbar={false}
        />
      </PageLayout.Content>
    </PageLayout>
  );
}

DispenseReportExplorer.propTypes = {
  onHideLoading: PropTypes.func.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
  modalStateActionShowModal: PropTypes.func.isRequired,
  modalStateActionHideModal: PropTypes.func.isRequired,
  dispenseReportExplorerBusinessRead: PropTypes.func.isRequired,
  dispenseReportExplorerBusinessDelete: PropTypes.func.isRequired,
  dispenseReportExplorerBusinessDownload: PropTypes.func.isRequired,
  dispenseReportUpdateDispenseReport: PropTypes.func.isRequired,
  intl: PropTypes.any.isRequired,
};

DispenseReportExplorer.defaultProps = {};

export default connect(null, mapDispatchToProps)(withIntl(DispenseReportExplorer));
